import { httpClientVendasCoreBff } from 'vendas-core/shared/services'
import {
  type WebhookEvent,
  type WebhookEventOrigin,
  type WebhookEventsFilters,
  type WebhookEventsListResponse,
  type WebhookEventStatus
} from 'webhooks/shared/entities'

import { useInfiniteQueryRequest } from '~/ui/hooks/utils'

async function getWebhookEvents(paymentProfileId: string | undefined, filters: WebhookEventsFilters, page: number) {
  return httpClientVendasCoreBff<WebhookEventsListResponse>({
    url: `/v1/payment-profile/${paymentProfileId}/webhooks`,
    method: 'GET',
    params: {
      page,
      page_size: 30,
      ...(filters.since && { since: filters.since }),
      ...(filters.until && { until: filters.until }),
      ...(filters.status && filters.status.length > 0 && { status: filters.status }),
      ...(filters.originType && filters.originType.length > 0 && { origin_type: filters.originType }),
      ...(filters.originId && filters.originId.length > 0 && { origin_id: filters.originId })
    },
    paramsSerializer: {
      indexes: null
    }
  })
}

export function useGetWebhookEvents(
  paymentProfileId: string | undefined,
  filters: WebhookEventsFilters,
  options?: { enabled?: boolean }
) {
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest } =
    useInfiniteQueryRequest<WebhookEventsListResponse>(
      ['useGetWebhookEvents', paymentProfileId, filters],
      ({ pageParam = 1 }) => getWebhookEvents(paymentProfileId, filters, pageParam as number),
      {
        getNextPageParam: lastPage => {
          const { currentPage, totalPage } = lastPage.data.pagination
          return currentPage < totalPage ? currentPage + 1 : undefined
        },
        enabled: Boolean(paymentProfileId) && (options?.enabled ?? true)
      }
    )

  const items: WebhookEvent[] =
    data?.pages.flatMap(page =>
      (page.data.items ?? []).map(item => ({
        id: item.id ?? '',
        event: item.event ?? '',
        status: (item.status ?? '') as WebhookEventStatus,
        origin: (item.origin?.type ?? '') as WebhookEventOrigin,
        createdAt: item.createdAt,
        externalId: item.externalId ?? ''
      }))
    ) ?? []

  return { items, fetchNextPage, hasNextPage, isFetchingNextPage, ...rest }
}
